import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { FaSave, FaEye, FaCog, FaHandshake, FaImage, FaTrash, FaUpload } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useSalon } from '../../contexts/SalonContext';
import { ROLES } from '../../utils/roles';
import './BookingSettings.css';

interface OnlineBookingSettings {
    title: string;
    description: string;
    fields: {
        customerName: { label: string; placeholder: string };
        phone: { label: string; placeholder: string };
        email: { label: string; placeholder: string };
        service: { label: string; placeholder: string };
        date: { label: string; placeholder: string };
        time: { label: string; placeholder: string };
        staff: { label: string; placeholder: string };
        referrer: { label: string; placeholder: string; visible: boolean };
        notes: { label: string; placeholder: string };
    };
    footerText: string;
}

const MAX_ILLUSTRATIONS = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const BookingSettings: React.FC = () => {
    const { user } = useAuth();
    const { currentBusiness, updateBusiness } = useSalon();

    const [settings, setSettings] = useState<OnlineBookingSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // --- Logo state ---
    const logoInputRef = useRef<HTMLInputElement>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoMsg, setLogoMsg] = useState<string | null>(null);

    // --- Banner state ---
    const bannerInputRef = useRef<HTMLInputElement>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [bannerMsg, setBannerMsg] = useState<string | null>(null);

    // --- Illustrations state ---
    const illustInputRef = useRef<HTMLInputElement>(null);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
    const [uploadingIllust, setUploadingIllust] = useState(false);
    const [illustMsg, setIllustMsg] = useState<string | null>(null);

    // TODO: wire to actual plan field when ready
    // const isPro = (user as any)?.plan === 'pro' || (currentBusiness as any)?.plan === 'pro';
    const isPro = true; // always visible for now — gate at backend level via checkProPlan middleware

    const canEdit = user?.role === ROLES.OWNER || user?.role === ROLES.MANAGER || user?.role === ROLES.ADMIN;

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await apiService.getOnlineBookingSettings();
            if (response.data) setSettings(response.data);
            setLoading(false);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load settings.' });
            setLoading(false);
        }
    };

    const handleFieldChange = (fieldKey: string, property: 'label' | 'placeholder', value: string) => {
        if (!settings) return;
        setSettings({
            ...settings,
            fields: {
                ...settings.fields,
                [fieldKey]: { ...(settings.fields as any)[fieldKey], [property]: value }
            }
        });
    };

    const handleReferrerToggle = () => {
        if (!settings) return;
        setSettings({
            ...settings,
            fields: {
                ...settings.fields,
                referrer: { ...settings.fields.referrer, visible: !settings.fields.referrer.visible }
            }
        });
    };

    const handleGeneralChange = (key: keyof OnlineBookingSettings, value: string) => {
        if (!settings) return;
        setSettings({ ...settings, [key]: value });
    };

    const handleSave = async () => {
        if (!settings) return;
        setSaving(true);
        setMessage(null);
        try {
            await apiService.updateOnlineBookingSettings(settings);
            setMessage({ type: 'success', text: 'Settings saved successfully!' });
            setTimeout(() => setMessage(null), 3000);
        } catch {
            setMessage({ type: 'error', text: 'Failed to save settings.' });
        } finally {
            setSaving(false);
        }
    };

    // ---- Logo upload ----
    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_FILE_SIZE) { setLogoMsg('❌ File too large (max 5 MB).'); return; }
        setLogoPreview(URL.createObjectURL(file));
        try {
            setUploadingLogo(true);
            setLogoMsg('⏳ Uploading logo...');
            const { url } = await apiService.uploadBusinessLogo(file);
            const updatedSettings = { ...currentBusiness?.settings, logo: url };
            await updateBusiness(currentBusiness!.id, { settings: updatedSettings });
            setLogoMsg('✅ Logo updated successfully!');
            setLogoPreview(null);
        } catch (err: any) {
            setLogoMsg('❌ ' + (err.message || 'Upload failed'));
        } finally {
            setUploadingLogo(false);
            e.target.value = '';
        }
    };

    // ---- Banner upload ----
    const handleBannerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_FILE_SIZE) { setBannerMsg('❌ File too large (max 5 MB).'); return; }
        setBannerPreview(URL.createObjectURL(file));
        try {
            setUploadingBanner(true);
            setBannerMsg('⏳ Uploading banner...');
            const { url } = await apiService.uploadBanner(file);
            const updatedSettings = { ...currentBusiness?.settings, banner: url };
            await updateBusiness(currentBusiness!.id, { settings: updatedSettings });
            setBannerMsg('✅ Banner updated successfully!');
            setBannerPreview(null);
        } catch (err: any) {
            setBannerMsg('❌ ' + (err.message || 'Upload failed'));
        } finally {
            setUploadingBanner(false);
            e.target.value = '';
        }
    };

    // ---- Illustrations ----
    const handleIllustChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        if (!files.length) return;
        const existingCount = (currentBusiness?.illustrations?.length || 0) + pendingFiles.length;
        if (existingCount + files.length > MAX_ILLUSTRATIONS) {
            setIllustMsg(`❌ Maximum ${MAX_ILLUSTRATIONS} illustrations allowed.`); return;
        }
        const oversized = files.find(f => f.size > MAX_FILE_SIZE);
        if (oversized) { setIllustMsg('❌ One or more files exceed 5 MB.'); return; }
        setPendingFiles(prev => [...prev, ...files]);
        setPendingPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
        setIllustMsg(null);
        e.target.value = '';
    };

    const removePending = (idx: number) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== idx));
        setPendingPreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const removeSavedIllustration = async (index: number) => {
        if (!currentBusiness) return;
        if (!window.confirm("Are you sure you want to delete this image?")) return;
        
        const newIllustrations = [...(currentBusiness.illustrations || [])];
        newIllustrations.splice(index, 1);
        try {
            setIllustMsg('⏳ Deleting image...');
            await updateBusiness(currentBusiness.id, { illustrations: newIllustrations });
            setIllustMsg('✅ Image deleted successfully!');
        } catch (err: any) {
            setIllustMsg('❌ Delete error: ' + (err.message || 'Failed'));
        }
    };

    const uploadIllustrations = async () => {
        if (!pendingFiles.length) return;
        try {
            setUploadingIllust(true);
            setIllustMsg('⏳ Uploading illustrations...');
            const { urls } = await apiService.uploadIllustrations(pendingFiles);
            const merged = [...(currentBusiness?.illustrations || []), ...urls];
            await updateBusiness(currentBusiness!.id, { illustrations: merged });
            setIllustMsg(`✅ Added ${urls.length} image(s)!`);
            setPendingFiles([]);
            setPendingPreviews([]);
        } catch (err: any) {
            setIllustMsg('❌ ' + (err.message || 'Upload failed'));
        } finally {
            setUploadingIllust(false);
        }
    };

    if (loading) return <div className="settings-loading">Loading settings...</div>;
    if (!settings) return <div className="settings-error">Could not load settings.</div>;

    if (!canEdit) {
        return (
            <div className="booking-settings-container">
                <div className="settings-error-card">
                    <h2>⚠️ Access Denied</h2>
                    <p>You do not have permission to manage online booking settings.</p>
                    <button className="btn-save" onClick={() => window.history.back()}>Go Back</button>
                </div>
            </div>
        );
    }

    const currentLogo = logoPreview || currentBusiness?.settings?.logo;
    const currentBanner = bannerPreview || currentBusiness?.settings?.banner;
    const savedIllustrations: string[] = currentBusiness?.illustrations || [];
    const totalIllustrations = savedIllustrations.length + pendingFiles.length;

    return (
        <div className="booking-settings-container">
            <div className="settings-header">
                <div className="header-title">
                    <FaCog className="title-icon" />
                    <h1>Online Booking Settings</h1>
                </div>
                <div className="header-actions">
                    <button className="btn-preview" onClick={() => window.open(`/book/${currentBusiness?.bookingCode}`, '_blank')} disabled={!currentBusiness?.bookingCode}>
                        <FaEye /> Preview Page
                    </button>
                    <button className="btn-save" onClick={handleSave} disabled={saving}>
                        <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {message && (
                <div className={`message-banner ${message.type}`}>{message.text}</div>
            )}

            <div className="settings-grid">
                {/* ══════ GENERAL APPEARANCE ══════ */}
                <section className="settings-card">
                    <h2><FaCog /> General Appearance</h2>

                    <div className="form-group">
                        <label>Page Title</label>
                        <input
                            type="text"
                            value={settings.title}
                            onChange={(e) => handleGeneralChange('title', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Welcome Message (Description)</label>
                        <textarea
                            value={settings.description}
                            onChange={(e) => handleGeneralChange('description', e.target.value)}
                            rows={3}
                        />
                    </div>
                    <div className="form-group">
                        <label>Footer Text</label>
                        <input
                            type="text"
                            value={settings.footerText}
                            onChange={(e) => handleGeneralChange('footerText', e.target.value)}
                        />
                    </div>

                    {/* ── DIVIDER ── */}
                    <div className="appearance-divider">
                        <FaImage /> Booking Page Images
                        {!isPro && (
                            <span className="pro-badge">✨ Pro</span>
                        )}
                    </div>

                    {!isPro ? (
                        <div className="pro-locked-notice">
                            🔒 Image upload is available on the <strong>Pro</strong> plan. Upgrade to customize your logo, banner, and illustration gallery.
                        </div>
                    ) : (
                        <div className="image-upload-section">
                            {/* Logo */}
                            <div className="upload-row">
                                <div className="upload-label-col">
                                    <span className="upload-label">Logo</span>
                                    <span className="upload-hint">Shown at the top of your booking page · max 5 MB</span>
                                </div>
                                <div className="upload-control-col">
                                    {currentLogo ? (
                                        <div className="image-chip">
                                            <img src={currentLogo} alt="Logo" className="chip-img" />
                                            <button
                                                className="chip-change-btn"
                                                onClick={() => logoInputRef.current?.click()}
                                                disabled={uploadingLogo}
                                            >
                                                {uploadingLogo ? '⏳' : '✏️ Change'}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="upload-btn"
                                            onClick={() => logoInputRef.current?.click()}
                                            disabled={uploadingLogo}
                                        >
                                            <FaUpload /> {uploadingLogo ? 'Uploading...' : 'Choose logo'}
                                        </button>
                                    )}
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                        style={{ display: 'none' }}
                                        onChange={handleLogoChange}
                                    />
                                    {logoMsg && <p className="upload-msg">{logoMsg}</p>}
                                </div>
                            </div>

                            {/* Banner */}
                            <div className="upload-row">
                                <div className="upload-label-col">
                                    <span className="upload-label">Banner</span>
                                    <span className="upload-hint">Hero section background · max 5 MB</span>
                                </div>
                                <div className="upload-control-col">
                                    {currentBanner ? (
                                        <div className="image-chip banner-chip">
                                            <img src={currentBanner} alt="Banner" className="chip-img banner-thumb" />
                                            <button
                                                className="chip-change-btn"
                                                onClick={() => bannerInputRef.current?.click()}
                                                disabled={uploadingBanner}
                                            >
                                                {uploadingBanner ? '⏳' : '✏️ Change'}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="upload-btn"
                                            onClick={() => bannerInputRef.current?.click()}
                                            disabled={uploadingBanner}
                                        >
                                            <FaUpload /> {uploadingBanner ? 'Uploading...' : 'Choose banner'}
                                        </button>
                                    )}
                                    <input
                                        ref={bannerInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                        style={{ display: 'none' }}
                                        onChange={handleBannerChange}
                                    />
                                    {bannerMsg && <p className="upload-msg">{bannerMsg}</p>}
                                </div>
                            </div>

                            {/* Illustrations */}
                            <div className="upload-row illustrations-row">
                                <div className="upload-label-col">
                                    <span className="upload-label">Illustrations</span>
                                    <span className="upload-hint">Carousel on booking page · max {MAX_ILLUSTRATIONS} images · 5 MB each</span>
                                    <span className="upload-hint" style={{ marginTop: '5px', fontWeight: 'bold', color: totalIllustrations >= MAX_ILLUSTRATIONS ? '#ef4444' : '#10b981' }}>
                                        Uploaded: {totalIllustrations} / {MAX_ILLUSTRATIONS} images
                                    </span>
                                </div>
                                <div className="upload-control-col full-width-control">
                                    {/* Saved thumbnails */}
                                    {savedIllustrations.length > 0 && (
                                        <div className="illust-grid">
                                            {savedIllustrations.map((src, i) => (
                                                <div key={i} className="illust-thumb saved">
                                                    <img src={src} alt={`Illust ${i + 1}`} />
                                                    <button
                                                        className="illust-delete-btn"
                                                        onClick={() => removeSavedIllustration(i)}
                                                        title="Delete image"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Pending thumbnails */}
                                    {pendingPreviews.length > 0 && (
                                        <div className="illust-grid pending">
                                            {pendingPreviews.map((src, i) => (
                                                <div key={i} className="illust-thumb pending">
                                                    <img src={src} alt={`Pending ${i + 1}`} />
                                                    <button
                                                        className="illust-delete-btn"
                                                        onClick={() => removePending(i)}
                                                        title="Remove"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="illust-actions">
                                        {totalIllustrations < MAX_ILLUSTRATIONS && (
                                            <button
                                                className="upload-btn"
                                                onClick={() => illustInputRef.current?.click()}
                                                disabled={uploadingIllust}
                                            >
                                                <FaUpload /> Add images ({totalIllustrations}/{MAX_ILLUSTRATIONS})
                                            </button>
                                        )}
                                        {pendingFiles.length > 0 && (
                                            <button
                                                className="upload-btn primary"
                                                onClick={uploadIllustrations}
                                                disabled={uploadingIllust}
                                            >
                                                {uploadingIllust ? '⏳ Uploading...' : `💾 Upload ${pendingFiles.length} image(s)`}
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        ref={illustInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={handleIllustChange}
                                    />
                                    {illustMsg && <p className="upload-msg">{illustMsg}</p>}
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {/* ══════ REFERRER FIELD ══════ */}
                <section className="settings-card">
                    <h2><FaHandshake /> Referrer Field</h2>
                    <div className="toggle-group">
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={settings.fields.referrer.visible}
                                onChange={handleReferrerToggle}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span>Enable "Referrer" field on booking page</span>
                    </div>
                    {settings.fields.referrer.visible && (
                        <div className="field-edit-row">
                            <div className="form-group">
                                <label>Field Label</label>
                                <input
                                    type="text"
                                    value={settings.fields.referrer.label}
                                    onChange={(e) => handleFieldChange('referrer', 'label', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Placeholder</label>
                                <input
                                    type="text"
                                    value={settings.fields.referrer.placeholder}
                                    onChange={(e) => handleFieldChange('referrer', 'placeholder', e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </section>

                {/* ══════ FORM FIELD CUSTOMIZATION ══════ */}
                <section className="settings-card full-width">
                    <h2>Form Field Customization</h2>
                    <p className="section-hint">Customize labels and placeholders for standard fields.</p>
                    <div className="fields-customizer">
                        {Object.entries(settings.fields).map(([key, field]) => {
                            if (key === 'referrer') return null;
                            const f = field as { label: string, placeholder: string };
                            return (
                                <div key={key} className="field-config-item">
                                    <div className="field-name">
                                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                                    </div>
                                    <div className="field-inputs">
                                        <div className="form-group">
                                            <label>Label</label>
                                            <input
                                                type="text"
                                                value={f.label}
                                                onChange={(e) => handleFieldChange(key, 'label', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Placeholder</label>
                                            <input
                                                type="text"
                                                value={f.placeholder}
                                                onChange={(e) => handleFieldChange(key, 'placeholder', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default BookingSettings;
